import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Pagination,
  Paper,
  Stack,
  Text
} from '@mantine/core'
import { IconSquare, IconSquareCheck } from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { useGetApplicationList } from '@/queries/applicationQueries'
import { buildCreateDeploymentLink } from '@/router/paths'
import { Application } from '@/types/app'
import { getPageCount, getPageOffset } from '@/utils/pagination'

const PAGE_SIZE = 5

type CreateDeploymentModalProps = {
  opened: boolean
  onClose: () => void
}

export const CreateDeploymentModal = ({
  opened,
  onClose
}: CreateDeploymentModalProps) => {
  const navigate = useNavigate()

  const [activePage, setActivePage] = useState(1)

  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null)

  const { data, isLoading, isError } = useGetApplicationList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: 'name'
    },
    {
      keepPreviousData: true,
      enabled: opened
    }
  )

  const applications = data?.results || []
  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  const reset = () => {
    setSelectedApplication(null)
    setActivePage(1)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCreateDeployment = () => {
    if (!selectedApplication) {
      return
    }

    void navigate(buildCreateDeploymentLink(selectedApplication.id))
  }

  return (
    <Modal
      size="xl"
      opened={opened}
      title={<FormattedMessage id="deployments.create" />}
      onClose={handleClose}
    >
      <Stack>
        <Text size="sm">
          <FormattedMessage id="deployments.create.description" />
        </Text>

        {applications.length === 0 && !isLoading && !isError && (
          <Text size="sm" fs="italic">
            <FormattedMessage id="deployments.create.noApplications" />
          </Text>
        )}

        {applications.length > 0 && (
          <Box>
            <Paper p="lg" withBorder>
              <Stack gap="sm">
                {/* TODO: Filter out applications that already have a deployment. We can't do this yet because the API doesn't provide this info. */}
                {applications.map((application) => (
                  <Button
                    key={application.id}
                    size="md"
                    variant="light"
                    justify="start"
                    color={
                      selectedApplication?.id === application.id
                        ? 'green'
                        : 'brand-primary'
                    }
                    leftSection={
                      selectedApplication?.id === application.id ? (
                        <IconSquareCheck
                          size={24}
                          color="var(--mantine-color-green-6)"
                        />
                      ) : (
                        <IconSquare
                          size={24}
                          color="var(--mantine-color-gray-6)"
                        />
                      )
                    }
                    onClick={() => setSelectedApplication(application)}
                  >
                    {application.name}
                  </Button>
                ))}
              </Stack>
            </Paper>

            <Flex justify="end" mt="md">
              <Pagination
                withPages={false}
                size="md"
                total={totalPageCount}
                value={activePage}
                onChange={(value) => setActivePage(value)}
              />
            </Flex>
          </Box>
        )}
      </Stack>

      <Group justify="end" mt="xl">
        <Button miw={120} variant="default" onClick={handleClose}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button
          miw={120}
          disabled={!selectedApplication}
          onClick={handleCreateDeployment}
        >
          <FormattedMessage id="deployments.create" />
        </Button>
      </Group>
    </Modal>
  )
}
